import { React, useState, useEffect, useCallback, useRef } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    AdminCommand,
    AdminMlLogs,
    AdminMlQueueStats,
    AdminObjectList,
    AdminObjectAdd,
    AdminObjectUpdate,
    AdminObjectRemove,
} from '../../../api';
import { AdminMlTemplates, AdminMlResync, AdminMlAwsLaunchTemplates } from '../../../api/admin-ml';
import { AdminMenuBar, AutoRefresh, PillTabs } from '../../';
import { storage } from '@goldblack/client-storage';

const ML_TABS = [
    { value: 'monitor', label: 'Monitor' },
    { value: 'bootstrap', label: 'Bootstrap' },
    { value: 'autoscaler', label: 'Autoscaler' },
];

const BOOTSTRAP_EMPTY = { id: '', image: '', command: '', env: '', port: '', health_path: '/health', swap_gb: '' };
const AUTOSCALER_EMPTY = {
    id: '',
    queue: '',
    min_instances: '',
    max_instances: '',
    aws_launch_template_id: '',
};

const TEST_CMD = {
    'nudenet-image': 'moderation_test',
    'nudenet-video': 'video_test',
    siglip2: 'embedding_test',
    'bert-small-toxicity': 'text_moderation_test',
};
// Template id → ml_jobs pipeline (mirrors STAGE_BY_QUEUE in schedule-ml-autoscale.js)
const STAGE_BY_TEMPLATE_ID = {
    'nudenet-image': 'moderation',
    'nudenet-video': 'video',
    siglip2: 'embedding',
    'bert-small-toxicity': 'text',
};

// Items done / items in the batch. The item in progress counts by its frame fraction (a video at 40% is 0.4/1, 24
// comments of 50 is 24/50). Once a box has run a batch the size stays: idle it shows 0/<size of the last batch>. A box that
// has not run one yet, or runs an older image, shows '—' (or the frame percentage while busy).
const fmtProgress = (m) => {
    if (m?.batch_total > 0) {
        const current = m.busy && (m.frames_total ?? 0) > 1 ? Math.min(1, (m.frames_done ?? 0) / m.frames_total) : 0;
        return `${Number(((m.batch_done ?? 0) + current).toFixed(1))}/${m.batch_total}`;
    }
    if (!m?.busy) return null;
    const total = Math.max(1, m.frames_total ?? 1);
    return `${Math.round(((m.frames_done ?? 0) / total) * 100)}%`;
};

// healthy = AWS checks passed + container answering (green); running = booted but not ready yet (orange)
const STATUS_COLOR = { healthy: 'success', running: 'warning' };
// Instance-row chips: status, AWS checks (3/3), docker (up) — same chips as the Infrastructure > AWS page.
const CHIP_SX = { height: 18, fontSize: 10 };

// EC2 public DNS (ec2-3-86-29-123.compute-1.amazonaws.com) → 3.86.29.123
const fmtAddr = (i, port) => {
    const ip =
        i.public_ip ??
        i.host
            ?.match(/^ec2-(\d+)-(\d+)-(\d+)-(\d+)\./)
            ?.slice(1)
            .join('.');
    return `${ip ?? i.host ?? i.id}:${port}`;
};

function StageBox({ title, children, sx }) {
    return (
        <Box
            component='fieldset'
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, m: 0, minWidth: 140, ...sx }}>
            <Box component='legend' sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', px: 0.5 }}>
                {title}
            </Box>
            {children}
        </Box>
    );
}

const red = (n) => (
    <Box component='span' sx={{ color: 'error.main' }}>
        {n}
    </Box>
);

function StatRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.15 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: 11 }}>{value ?? 0}</Typography>
        </Box>
    );
}

function Arrow() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', px: 0.5, flexShrink: 0 }}>
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Box>
    );
}

function Pipeline({ template: t, stageStats, instances, onLog }) {
    const title = t?.queue ?? t?.id ?? '—';
    const testCmd = TEST_CMD[t?.id?.toLowerCase()];

    return (
        <Box component='fieldset' sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, m: 0 }}>
            <Box component='legend' sx={{ fontSize: 16, color: 'text.primary', px: 0.5 }}>
                {title}
            </Box>
            {/* No loading placeholder ('…') on refresh — keep the last value so the numbers
                don't flicker on each 5s poll; loading is only a background swap. */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StageBox title='JOBS'>
                    <StatRow label='Queued' value={stageStats?.waiting ?? 0} />
                    <StatRow
                        label='Processing'
                        value={
                            (stageStats?.batches ?? 0) > 0 && stageStats.batches !== stageStats.processing
                                ? `${stageStats.processing} (${stageStats.batches} boxes)`
                                : (stageStats?.processing ?? 0)
                        }
                    />
                    {/* Age of the oldest queued job (the AUTOSCALER's Target Age shows it against the target). */}
                    <StatRow
                        label='Oldest'
                        value={stageStats?.oldest_wait_sec == null ? '—' : `${stageStats.oldest_wait_sec} s`}
                    />
                </StageBox>

                {t && (
                    <>
                        <Arrow />
                        <StageBox title='AUTOSCALER'>
                            <StatRow label='Min' value={t.min_instances ?? 0} />
                            <StatRow label='Max' value={t.max_instances ?? 0} />
                            <StatRow
                                label='Target Queue'
                                value={
                                    <>
                                        {(stageStats?.waiting ?? 0) > (t.target_backlog ?? 0)
                                            ? red(stageStats?.waiting ?? 0)
                                            : (stageStats?.waiting ?? 0)}
                                        /{t.target_backlog ?? 0}
                                    </>
                                }
                            />
                            <StatRow
                                label='Target Age'
                                value={
                                    <>
                                        {(stageStats?.oldest_wait_sec ?? 0) > (t.target_wait_min ?? 1) * 60
                                            ? red(stageStats?.oldest_wait_sec ?? 0)
                                            : (stageStats?.oldest_wait_sec ?? 0)}
                                        /{(t.target_wait_min ?? 1) * 60} s
                                    </>
                                }
                            />
                            <StatRow
                                label='Scale Up'
                                value={
                                    <>
                                        {t.pending_up_since
                                            ? red(Math.round((Date.now() - t.pending_up_since) / 1000))
                                            : 0}
                                        /{t.scale_up_hold ?? 180} s
                                    </>
                                }
                            />
                            <StatRow
                                label='Scale Down'
                                value={
                                    <>
                                        {t.pending_down_since
                                            ? red(Math.round((Date.now() - t.pending_down_since) / 1000))
                                            : 0}
                                        /{t.scale_down_hold ?? 600} s
                                    </>
                                }
                            />
                        </StageBox>

                        <Arrow />
                        <StageBox title={`INSTANCES (${instances.length})`}>
                            {instances.length === 0 ? (
                                <Typography variant='caption' color='text.disabled'>
                                    no instances
                                </Typography>
                            ) : (
                                <Table size='small' sx={{ '& td, & th': { border: 0, py: 0.25, px: 1, fontSize: 11 } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Address
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Region
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Status
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>CPU</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Mem</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Progress
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>RPM</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Total
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {instances.map((i, idx) => (
                                            <TableRow key={i.id}>
                                                <TableCell sx={{ color: 'text.disabled' }}>{idx + 1}</TableCell>
                                                <TableCell>{fmtAddr(i, t.port)}</TableCell>
                                                <TableCell>{i.region ?? '—'}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        {/* Only when there is nothing else to show (e.g. pending), fall back to the lifecycle state. */}
                                                        {!(i.checks?.total > 0) && !i.docker && (
                                                            <Chip
                                                                label={i.status ?? '—'}
                                                                size='small'
                                                                color={STATUS_COLOR[i.status] ?? 'default'}
                                                                sx={CHIP_SX}
                                                            />
                                                        )}
                                                        {i.checks?.total > 0 && (
                                                            <Chip
                                                                label={`${i.checks.ok}/${i.checks.total}`}
                                                                size='small'
                                                                color={
                                                                    i.checks.ok === i.checks.total
                                                                        ? 'success'
                                                                        : 'warning'
                                                                }
                                                                sx={CHIP_SX}
                                                            />
                                                        )}
                                                        {i.docker && (
                                                            <Chip
                                                                label={i.docker}
                                                                size='small'
                                                                color={i.docker === 'up' ? 'success' : 'warning'}
                                                                sx={CHIP_SX}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {i.metrics?.cpu_percent != null
                                                        ? `${i.metrics.cpu_percent}%`
                                                        : '0%'}
                                                </TableCell>
                                                <TableCell>
                                                    {i.metrics?.memory_used_pct != null
                                                        ? `${i.metrics.memory_used_pct}%`
                                                        : '0%'}
                                                </TableCell>
                                                <TableCell>{fmtProgress(i.metrics) ?? '—'}</TableCell>
                                                {/* Counted by the box itself (/metrics); '—' on a box running an older image. */}
                                                <TableCell>{i.metrics?.calls_per_min ?? '—'}</TableCell>
                                                <TableCell>{i.metrics?.calls_total?.toLocaleString() ?? '—'}</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                        {testCmd && (i.host || i.public_ip) && (
                                                            <Button
                                                                size='small'
                                                                variant='outlined'
                                                                onClick={() => {
                                                                    storage.transience.setState('consoleLog', []);
                                                                    AdminCommand(testCmd, [
                                                                        'loise',
                                                                        `http://${i.host ?? i.public_ip}:${t.port}/`,
                                                                    ]);
                                                                }}
                                                                sx={{ py: 0, fontSize: 10, minWidth: 0 }}>
                                                                Test
                                                            </Button>
                                                        )}
                                                        {(i.host || i.public_ip) && (
                                                            <Button
                                                                size='small'
                                                                variant='outlined'
                                                                onClick={() => onLog(i, t)}
                                                                sx={{ py: 0, fontSize: 10, minWidth: 0 }}>
                                                                Log
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </StageBox>
                    </>
                )}
            </Box>
        </Box>
    );
}

function MiniConsole() {
    const logs = storage.transience.getValue('consoleLog');
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
    }, [logs?.length]);

    return (
        <Box
            component='fieldset'
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}>
            <Box component='legend' sx={{ fontSize: 16, color: 'text.primary', px: 0.5 }}>
                console
            </Box>
            <Box
                ref={ref}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    color: 'text.primary',
                    fontFamily: 'monospace',
                    fontSize: 11,
                }}>
                {logs?.map((msg, i) => (
                    <Box key={i} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.5 }}>
                        {msg}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function CrudPanel({ items, selected, setSelected, form, setForm, fields }) {
    const select = (item) => {
        setSelected(item._id);
        setForm({ ...item });
    };

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRight: 1,
                    borderColor: 'primary.main',
                    overflow: 'auto',
                    boxSizing: 'border-box',
                }}>
                <List disablePadding>
                    {items.map((item) => (
                        <ListItem key={item._id} disablePadding>
                            <ListItemButton selected={selected === item._id} onClick={() => select(item)}>
                                <ListItemText primary={item.id} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', boxSizing: 'border-box' }}>
                <TableContainer sx={{ overflowX: 'hidden' }}>
                    <Table
                        sx={{
                            tableLayout: 'fixed',
                            width: '100%',
                            '& td, & th': { border: 1, borderColor: 'divider' },
                        }}>
                        <TableBody>
                            {fields.map(({ key, label, type, required, options }) => (
                                <TableRow key={key}>
                                    <TableCell sx={{ width: '30%' }}>{label}</TableCell>
                                    <TableCell sx={{ width: '70%' }}>
                                        {options ? (
                                            <Autocomplete
                                                options={options}
                                                value={options.find((o) => o.id === form[key]) ?? null}
                                                onChange={(_, v) => setForm((f) => ({ ...f, [key]: v?.id ?? '' }))}
                                                getOptionLabel={(o) => o.label}
                                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                                size='small'
                                                fullWidth
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size='small'
                                                        variant='standard'
                                                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <TextField
                                                size='small'
                                                fullWidth
                                                required={required}
                                                type={type ?? 'text'}
                                                value={form[key] ?? ''}
                                                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                                                InputProps={{
                                                    readOnly: key === 'id' && !!selected,
                                                    disableUnderline: true,
                                                }}
                                                variant='standard'
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}

const BOOTSTRAP_FIELDS = [
    { key: 'id', label: 'ID', required: true },
    { key: 'image', label: 'Image' },
    { key: 'command', label: 'Command' },
    { key: 'env', label: 'Env (JSON)' },
    { key: 'port', label: 'Port', type: 'number', required: true },
    { key: 'health_path', label: 'Health Path' },
    { key: 'swap_gb', label: 'Swap GB', type: 'number' },
];

const AUTOSCALER_FIELDS = [
    { key: 'id', label: 'ID', required: true },
    { key: 'queue', label: 'Queue', required: true },
    { key: 'min_instances', label: 'Min Instances', type: 'number' },
    { key: 'max_instances', label: 'Max Instances', type: 'number', required: true },
    { key: 'aws_launch_template_id', label: 'AWS Launch Template ID' },
];

function LoiseMl() {
    const [tab, setTab] = useState('monitor');
    const [mlQueueStats, setMlQueueStats] = useState({ loading: false });
    const [templates, setTemplates] = useState({ loading: false });
    const [resyncing, setResyncing] = useState(false);
    const [bootstrapForm, setBootstrapForm] = useState({ ...BOOTSTRAP_EMPTY });
    const [bootstrapSelected, setBootstrapSelected] = useState(null);
    const [autoscalerForm, setAutoscalerForm] = useState({ ...AUTOSCALER_EMPTY });
    const [autoscalerSelected, setAutoscalerSelected] = useState(null);

    const account = storage.transience.getValue('account');
    const bootstrapItems = account?.mlBootstrap?.data ?? [];
    const autoscalerItems = account?.mlAutoscaler?.data ?? [];

    const launchTemplateOptions = (storage.transience.getValue('infrastructure')?.awsLaunchTemplates ?? []).map(
        (t) => ({ label: `${t.name} (${t.id})`, id: t.id }),
    );
    const autoscalerFields = AUTOSCALER_FIELDS.map((f) =>
        f.key === 'aws_launch_template_id' ? { ...f, options: launchTemplateOptions } : f,
    );

    const onResync = () => {
        setResyncing(true);
        AdminMlResync(() => {
            setResyncing(false);
            AdminMlTemplates(setTemplates);
        });
    };

    const openLog = useCallback((inst, t) => {
        const host = inst.host ?? inst.public_ip;
        storage.transience.setState('consoleLog', []);
        storage.transience.addState('consoleLog', `── ${fmtAddr(inst, t.port)} logs ──`);
        AdminMlLogs(
            host,
            t.port,
            (lines) => lines.forEach((l) => storage.transience.addState('consoleLog', l)),
            (err) => storage.transience.addState('consoleLog', `[error] ${err}`),
        );
    }, []);

    const refresh = useCallback(() => {
        AdminMlQueueStats(setMlQueueStats);
        AdminMlTemplates(setTemplates);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (tab === 'bootstrap') AdminObjectList('account', 'mlBootstrap');
        if (tab === 'autoscaler') {
            AdminObjectList('account', 'mlAutoscaler');
            AdminMlAwsLaunchTemplates('us-east-1');
        }
    }, [tab]);

    const stageStats = mlQueueStats.data ?? [];
    const rows = templates.data ?? [];

    const toNum = (v) => (v === '' || v == null ? null : Number(v));

    const menuItems = (() => {
        if (tab === 'bootstrap') {
            return [
                {
                    label: 'New',
                    onClick: () => {
                        setBootstrapSelected(null);
                        setBootstrapForm({ ...BOOTSTRAP_EMPTY });
                    },
                },
                bootstrapSelected
                    ? {
                          label: 'Update',
                          onClick: () =>
                              AdminObjectUpdate('account', 'mlBootstrap', {
                                  ...bootstrapForm,
                                  port: toNum(bootstrapForm.port),
                                  swap_gb: toNum(bootstrapForm.swap_gb),
                              }),
                      }
                    : {
                          label: 'Add',
                          onClick: () =>
                              AdminObjectAdd(
                                  'account',
                                  'mlBootstrap',
                                  {
                                      ...bootstrapForm,
                                      port: toNum(bootstrapForm.port),
                                      swap_gb: toNum(bootstrapForm.swap_gb),
                                  },
                                  () => {
                                      setBootstrapSelected(null);
                                      setBootstrapForm({ ...BOOTSTRAP_EMPTY });
                                  },
                              ),
                      },
                {
                    label: 'Delete',
                    disabled: !bootstrapSelected,
                    onClick: () => {
                        AdminObjectRemove('account', 'mlBootstrap', bootstrapSelected);
                        setBootstrapSelected(null);
                        setBootstrapForm({ ...BOOTSTRAP_EMPTY });
                    },
                },
            ];
        }
        if (tab === 'autoscaler') {
            return [
                {
                    label: 'New',
                    onClick: () => {
                        setAutoscalerSelected(null);
                        setAutoscalerForm({ ...AUTOSCALER_EMPTY });
                    },
                },
                autoscalerSelected
                    ? {
                          label: 'Update',
                          onClick: () =>
                              AdminObjectUpdate('account', 'mlAutoscaler', {
                                  ...autoscalerForm,
                                  min_instances: toNum(autoscalerForm.min_instances) ?? 0,
                                  max_instances: toNum(autoscalerForm.max_instances),
                              }),
                      }
                    : {
                          label: 'Add',
                          onClick: () =>
                              AdminObjectAdd(
                                  'account',
                                  'mlAutoscaler',
                                  {
                                      ...autoscalerForm,
                                      min_instances: toNum(autoscalerForm.min_instances) ?? 0,
                                      max_instances: toNum(autoscalerForm.max_instances),
                                  },
                                  () => {
                                      setAutoscalerSelected(null);
                                      setAutoscalerForm({ ...AUTOSCALER_EMPTY });
                                  },
                              ),
                      },
                {
                    label: 'Delete',
                    disabled: !autoscalerSelected,
                    onClick: () => {
                        AdminObjectRemove('account', 'mlAutoscaler', autoscalerSelected);
                        setAutoscalerSelected(null);
                        setAutoscalerForm({ ...AUTOSCALER_EMPTY });
                    },
                },
            ];
        }
        return [
            { label: resyncing ? 'Resyncing…' : 'Resync', onClick: onResync, disabled: resyncing },
            { label: 'Refresh', onClick: refresh },
        ];
    })();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <PillTabs value={tab} onChange={setTab} tabs={ML_TABS} />
                <AutoRefresh onRefresh={refresh} defaultMs={5_000} defaultOn />
            </Box>
            <AdminMenuBar apiItems={menuItems} />

            {tab === 'monitor' && (
                <Box sx={{ m: 2, display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0 }}>
                    {rows.map((t) => (
                        <Pipeline
                            key={t.id}
                            template={t}
                            stageStats={
                                stageStats.find((s) => s.stage === STAGE_BY_TEMPLATE_ID[t.id?.toLowerCase()]) ?? null
                            }
                            instances={t.instances ?? []}
                            onLog={openLog}
                        />
                    ))}

                    <MiniConsole />
                </Box>
            )}

            {tab === 'bootstrap' && (
                <Box sx={{ display: 'flex', flex: 1, minHeight: 300 }}>
                    <CrudPanel
                        items={bootstrapItems}
                        selected={bootstrapSelected}
                        setSelected={setBootstrapSelected}
                        form={bootstrapForm}
                        setForm={setBootstrapForm}
                        fields={BOOTSTRAP_FIELDS}
                    />
                </Box>
            )}

            {tab === 'autoscaler' && (
                <Box sx={{ display: 'flex', flex: 1, minHeight: 300 }}>
                    <CrudPanel
                        items={autoscalerItems}
                        selected={autoscalerSelected}
                        setSelected={setAutoscalerSelected}
                        form={autoscalerForm}
                        setForm={setAutoscalerForm}
                        fields={autoscalerFields}
                    />
                </Box>
            )}
        </Box>
    );
}

export { LoiseMl };
